import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAchievementDetail, useUpdateShowcase, useAchievements } from '../api/hooks';
import { BackIcon } from '../components/icons';
import { Medallion } from '../components/achievements/Medallion';
import { ErrorView } from '../components/StateViews';
import { backButton, haptics } from '../telegram';
import { track } from '../analytics';
import { seasonLabel, shortDate } from '../format';

/**
 * Страница достижения (D1): медальон с кольцом, чипы, лестница уровней,
 * для сезонных — предупреждение о фиксации и история «По сезонам»,
 * у владельца — «Закрепить в витрине».
 */
export function AchievementDetailScreen() {
  const navigate = useNavigate();
  const { pid: rawPid, id: rawId } = useParams();
  const pid = rawPid === undefined ? null : Number(rawPid);
  const id = Number(rawId);
  const detail = useAchievementDetail(pid, id);
  const summary = useAchievements(null);
  const updateShowcase = useUpdateShowcase();

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (detail.isLoading) {
    return (
      <div className="ach-screen">
        <div className="skeleton" style={{ height: 220, borderRadius: 18, marginBottom: 14 }} />
        <div className="skeleton" style={{ height: 200, borderRadius: 13 }} />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="ach-screen">
        <div className="subhead ach-head">
          <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
            <BackIcon />
          </button>
          <div className="title">Достижение</div>
        </div>
        <ErrorView message="Достижение не найдено" />
      </div>
    );
  }

  const a = detail.data;
  const isOwner = pid === null;
  const pct =
    a.progress !== null && a.nextThreshold && a.nextThreshold > 0
      ? Math.min(1, a.progress / a.nextThreshold)
      : null;

  const pin = async () => {
    haptics.tap();
    const current = (summary.data?.showcase ?? []).map((s) => s.id);
    if (current.includes(a.id) || current.length >= 3) return;
    try {
      await updateShowcase.mutateAsync([...current, a.id]);
      track('showcase_pin', { id: a.id, from: 'detail' });
      haptics.success();
    } catch {
      haptics.error();
    }
  };

  const pinned = a.pinned || (summary.data?.showcase ?? []).some((s) => s.id === a.id);
  const canPin =
    isOwner && a.level >= 1 && !pinned && (summary.data?.showcase ?? []).length < 3;

  return (
    <div className="ach-screen">
      <div className="subhead ach-head">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">Достижение</div>
      </div>

      <div className="ach-detail-hero">
        <Medallion icon={a.icon} level={a.level} maxLevel={a.maxLevel} progress={pct}
          state={a.state} masked={a.masked} size={68} />
        <div className="ach-detail-name">{a.name}</div>
        <div className="ach-chips">
          {a.kind === 'permanent' ? (
            <span className="ach-chip">Постоянное</span>
          ) : (
            <span className="ach-chip ach-chip--season">
              Сезонное{a.seasonId ? ` · ${seasonLabel(a.seasonId)}` : ''}
            </span>
          )}
          {a.maxLevel > 1 && (
            <span className="ach-chip ach-chip--gold">
              Ур. {a.level} из {a.maxLevel}
            </span>
          )}
          {a.state === 'active' && a.daysLeft !== null && (
            <span className="ach-chip ach-chip--deadline">Ещё {a.daysLeft} дн.</span>
          )}
          {a.state === 'fixed' && <span className="ach-chip ach-chip--gold">Зафиксировано</span>}
          {a.state === 'missed' && <span className="ach-chip ach-chip--missed">Упущено</span>}
          {a.paused && <span className="ach-chip">Временно недоступно</span>}
        </div>
        {a.description && <p className="ach-detail-descr">{a.description}</p>}
      </div>

      {!a.masked && a.ladder.length > 0 && (
        <div className="ach-ladder">
          <div className="ach-overline">Лестница уровней</div>
          {a.ladder.map((l) => {
            const passed = a.level >= l.n;
            const current = a.level + 1 === l.n && a.state !== 'missed' && a.state !== 'fixed';
            const label = l.title ?? `Уровень ${l.n}`;
            const condition = a.higherIsBetter
              ? `${l.threshold}`
              : `топ-${l.threshold}`;
            return (
              <div
                key={l.n}
                className={`ach-step${passed ? ' passed' : current ? ' current' : ' future'}`}
              >
                <span className="mark">{passed ? '✓' : current ? l.n : '•'}</span>
                <div className="body">
                  <div className="ttl">
                    {label} · {condition}
                  </div>
                  {passed && l.unlockedAt && (
                    <div className="sub">Получен {shortDate(l.unlockedAt)}</div>
                  )}
                  {current && a.progress !== null && a.nextThreshold !== null && (
                    <>
                      <div className="bar">
                        <i style={{ width: `${Math.round((pct ?? 0) * 100)}%` }} />
                      </div>
                      <div className="sub">
                        {a.higherIsBetter
                          ? `${a.progress} из ${a.nextThreshold} · осталось ${Math.max(0, a.nextThreshold - a.progress)}`
                          : `Сейчас: ${a.progress > 0 ? `место ${a.progress}` : 'вне рейтинга'} · цель: топ-${a.nextThreshold}`}
                      </div>
                    </>
                  )}
                </div>
                <span className="pts">+{l.points}</span>
              </div>
            );
          })}
        </div>
      )}

      {a.paused && (
        <div className="ach-season-warn">
          Достижение временно недоступно — начисление приостановлено. Заработанные уровни и
          очки сохраняются.
        </div>
      )}
      {a.kind === 'seasonal' && a.state === 'active' && !a.paused && (
        <div className="ach-season-warn">
          После конца сезона уровень зафиксируется навсегда — успей прокачать до финала.
        </div>
      )}

      {a.bySeasons.length > 1 && (
        <div className="ach-history">
          <div className="ach-overline">По сезонам</div>
          <div className="ach-history-chips">
            {a.bySeasons.map((s) => (
              <span
                key={s.seasonId}
                className={`ach-chip${s.state === 'active' ? ' ach-chip--season' : s.level > 0 ? ' ach-chip--gold' : ''}`}
              >
                {seasonLabel(s.seasonId)} ·{' '}
                {s.state === 'active' ? 'идёт' : s.level > 0 ? `ур. ${s.level}` : 'упущено'}
              </span>
            ))}
          </div>
        </div>
      )}

      {canPin && (
        <button className="ach-pin-btn" onClick={pin} disabled={updateShowcase.isPending}>
          Закрепить в витрине
        </button>
      )}
      {isOwner && pinned && <div className="ach-pinned-note">В витрине профиля</div>}
    </div>
  );
}
